import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import AnimateIn from "@/components/AnimateIn";
import WCButton from "@/components/WCButton";
import { Instagram, Youtube } from "lucide-react";
import { socialLinks, brandEmail } from "@/data/brand";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message received. We respond within 48 hours.");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch {
      toast.error("Something went wrong. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const change = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="bg-black" data-testid="contact-page">
      <section className="pt-[160px] md:pt-[200px] pb-24 px-6 md:px-12 text-center">
        <AnimateIn variant="fade">
          <h1 className="wc-heading text-white text-6xl md:text-8xl tracking-widest">CONTACT</h1>
          <p className="text-white/70 mt-6 text-base md:text-lg">
            We respond within 48 hours.
          </p>
        </AnimateIn>
      </section>

      <section className="px-6 md:px-12 pb-32">
        <div className="max-w-2xl mx-auto">
          <form onSubmit={submit} className="space-y-5" data-testid="contact-form">
            <Input
              label="NAME"
              value={form.name}
              onChange={change("name")}
              required
              testid="contact-name"
            />
            <Input
              label="EMAIL"
              type="email"
              value={form.email}
              onChange={change("email")}
              required
              testid="contact-email"
            />
            <Input
              label="SUBJECT"
              value={form.subject}
              onChange={change("subject")}
              required
              testid="contact-subject"
            />
            <div>
              <label className="wc-label text-xs text-white block mb-2">MESSAGE</label>
              <textarea
                rows={6}
                required
                value={form.message}
                onChange={change("message")}
                className="w-full bg-transparent border border-white/40 focus:border-white text-white outline-none px-5 py-4 text-base transition-colors"
                data-testid="contact-message"
              />
            </div>
            <WCButton type="submit" filled disabled={loading} data-testid="contact-submit">
              {loading ? "SENDING..." : "SUBMIT"}
            </WCButton>
          </form>

          <div className="mt-16 border-t border-white/10 pt-10">
            <div className="wc-label text-xs text-white/50">DIRECT</div>
            <a
              href={`mailto:${brandEmail}`}
              className="block wc-heading text-white text-2xl md:text-4xl tracking-widest mt-3"
              data-testid="contact-direct-email"
            >
              {brandEmail.toUpperCase()}
            </a>
            {(socialLinks.instagram || socialLinks.tiktok || socialLinks.youtube) && (
              <div className="flex gap-5 mt-8">
                {socialLinks.instagram && (
                  <a
                    href={socialLinks.instagram}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Instagram"
                    data-testid="contact-instagram"
                  >
                    <Instagram className="text-white hover:opacity-60" size={22} />
                  </a>
                )}
                {socialLinks.tiktok && (
                  <a
                    href={socialLinks.tiktok}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="TikTok"
                    data-testid="contact-tiktok"
                  >
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" className="text-white hover:opacity-60">
                      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1.84-.1z" />
                    </svg>
                  </a>
                )}
                {socialLinks.youtube && (
                  <a
                    href={socialLinks.youtube}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="YouTube"
                    data-testid="contact-youtube"
                  >
                    <Youtube className="text-white hover:opacity-60" size={24} />
                  </a>
                )}
              </div>
            )}
            <p className="text-white/50 text-sm mt-10">
              For press inquiries please include <span className="text-white">Press</span> in your
              subject line.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}

const Input = ({ label, testid, ...rest }) => (
  <div>
    <label className="wc-label text-xs text-white block mb-2">{label}</label>
    <input
      {...rest}
      data-testid={testid}
      className="w-full bg-transparent border border-white/40 focus:border-white text-white outline-none px-5 py-4 text-base transition-colors"
    />
  </div>
);
